<template>
  <ReservationDetailSkeleton
    v-if="modalState === MODAL_STATE.STATUS_CHANGING"
    id="status-changing-modal"
  />
  <SlotPanel
    v-else-if="selectedReservation"
    ref="reservationDetails"
    key="reservationDetails"
    id="performance-reservation-detail"
  >
    <div class="max-h-full overscroll-y-auto">
      <ReservationStatusDropdown
        v-if="!isReservationUnpaid"
        id="set-reservation-status-dropdown"
        :reservation="selectedReservation"
        @setReservationStatus="setReservationStatus"
      />
      <div class="flex justify-between" :class="{ 'mt-4': !isReservationUnpaid }">
        <ReservationActionsDropdown
          v-if="!isReservationUnpaid"
          id="reservation-actions-dropdown"
          class="w-1/2 pr-2"
          :reservationId="routeReservationId"
          @editReservation="$emit('editReservation')"
          @editGuest="setState(MODAL_STATE.EDIT_GUEST)"
          @refund="setState(MODAL_STATE.REFUNDING)"
          @resendEmail="setState(MODAL_STATE.RESEND_EMAIL)"
        />
        <div v-else class="w-1/2 pr-2">
          <ButtonDefault
            id="cancel-reservation-button"
            label="Cancel Reservation"
            class="w-full"
            @click="cancelUnpaidReservation"
            alt
          />
        </div>
        <div class="w-1/2 pl-2">
          <ButtonDefault
            id="close-button"
            label="Close"
            class="w-full"
            @click="$emit('close')"
            alt
          />
        </div>
      </div>
      <ReservationDetails id="reservation-details" class="mt-4" />
    </div>
    <ErrorDisplay id="error-message" class="mt-4" v-bind="ui.error" />

    <!--MODALS-->
    <SlotModal
      :display="modalState == MODAL_STATE.EDIT_GUEST"
      @close="setState(MODAL_STATE.NONE)"
      id="edit-guest-modal"
      width="w-full"
    >
      <template #header>Edit Guest</template>
      <GuestEditForm
        id="edit-guest-form"
        :contact="selectedReservation.contact"
        @cancel="setState(MODAL_STATE.NONE)"
        @updated="$emit('reservationUpdated')"
      />
    </SlotModal>

    <RefundModal
      id="refund-modal"
      :display="modalState === MODAL_STATE.REFUNDING"
      :reservation="selectedReservation"
      @close="setState(MODAL_STATE.NONE)"
    />

    <ResendConfirmationModal
      v-if="modalState === MODAL_STATE.RESEND_EMAIL"
      id="resend-confirmation-modal"
      :reservation="selectedReservation"
      @close="setState(MODAL_STATE.NONE)"
    />

    <SlotModal
      :display="modalState == MODAL_STATE.CLEAR_TABLE"
      id="clear-table-modal"
      @close="setState(MODAL_STATE.NONE)"
    >
      <template #header>Please Confirm</template>
      <div class="mb-8">Are you sure you want to clear table {{ selectedTablesName }}?</div>
      <div class="mb-2 flex justify-between">
        <ButtonDefault
          id="clear-table-cancel"
          label="Cancel"
          class="w-1/2 mr-4"
          @click="setState(MODAL_STATE.NONE)"
          alt
        />
        <ButtonDefault
          id="clear-table-confirm"
          label="Clear"
          class="w-1/2"
          @click="setReservationStatus(ReservationStatus.CLEARED)"
        />
      </div>
    </SlotModal>

    <SlotModal
      :display="modalState === MODAL_STATE.CANCELING"
      id="cancel-reservation-modal"
      @close="setState(MODAL_STATE.NONE)"
    >
      <template #header>Cancel Reservation</template>
      <div class="mb-8">Are you sure you want to cancel this reservation for {{ guestName }}?</div>
      <div class="mb-2 flex justify-between">
        <ButtonDefault
          id="cancel-reservation-confirm"
          label="Yes"
          @click="setReservationStatus(ReservationStatus.CANCELED)"
        />
        <ButtonDefault label="No" alt @click="setState(MODAL_STATE.NONE)" />
      </div>
    </SlotModal>
  </SlotPanel>
</template>

<script lang="ts">
import { mapActions, mapState, mapStores } from 'pinia'
import { defineComponent } from 'vue'

import ButtonDefault from '@/common/components/atoms/ButtonDefault.vue'
import ErrorDisplay from '@/common/components/atoms/ErrorDisplay.vue'
import SlotModal from '@/common/components/organisms/SlotModal.vue'
import SlotPanel from '@/common/components/organisms/SlotPanel.vue'
import GuestEditForm from '@/components/guest/GuestEditForm.vue'
import { usePerformanceReservationStore } from '@/modules/performance/stores/PerformanceReservationStore'
import ReservationActionsDropdown from '@/modules/reservation/components/molecules/ReservationActionsDropdown.vue'
import ReservationStatusDropdown from '@/modules/reservation/components/molecules/ReservationStatusDropdown.vue'
import RefundModal from '@/modules/reservation/components/organisms/RefundModal.vue'
import ResendConfirmationModal from '@/modules/reservation/components/organisms/ResendConfirmationModal.vue'
import ReservationDetailSkeleton from '@/modules/reservation/components/organisms/ReservationDetailSkeleton.vue'
import ReservationDetails from '@/modules/reservation/components/organisms/ReservationDetails.vue'
import { useReservationStore } from '@/modules/reservation/stores/ReservationStore'
import { blankError, displayHandled } from '@/utils/errors'

import { ReservationStatus } from '@generated/types'

type TReservationStatus = (typeof ReservationStatus)[keyof typeof ReservationStatus]
type TModalState = (typeof MODAL_STATE)[keyof typeof MODAL_STATE]

const MODAL_STATE = {
  NONE: 'none',
  EDIT_GUEST: 'edit-guest',
  REFUNDING: 'refund',
  RESEND_EMAIL: 'resend-email',
  CLEAR_TABLE: 'clear-table',
  CANCELING: 'canceling',
  STATUS_CHANGING: 'status-changing',
}

export default defineComponent({
  name: 'ReservationInformation',
  components: {
    ReservationDetailSkeleton,
    ErrorDisplay,
    GuestEditForm,
    ResendConfirmationModal,
    SlotModal,
    RefundModal,
    ButtonDefault,
    SlotPanel,
    ReservationActionsDropdown,
    ReservationStatusDropdown,
    ReservationDetails,
  },
  emits: {
    close: () => true,
    reservationUpdated: () => true,
    editReservation: () => true,
  },
  data() {
    return {
      modalState: MODAL_STATE.NONE,
      MODAL_STATE,
      ReservationStatus,
      ui: {
        error: blankError(),
      },
    }
  },
  methods: {
    ...mapActions(usePerformanceReservationStore, ['setSelectedReservation']),
    setState(state: TModalState) {
      this.modalState = state
    },
    async setReservationStatus(item: TReservationStatus) {
      try {
        if (item === ReservationStatus.CLEARED && this.modalState !== MODAL_STATE.CLEAR_TABLE) {
          this.setState(MODAL_STATE.CLEAR_TABLE)
        } else if (
          item === ReservationStatus.CANCELED &&
          this.modalState !== MODAL_STATE.CANCELING
        ) {
          this.setState(MODAL_STATE.CANCELING)
        } else {
          this.setState(MODAL_STATE.STATUS_CHANGING)
          const { reservation } = await this.reservationStore.setReservationStatus(
            this.selectedReservation!,
            item,
          )
          this.setSelectedReservation(reservation)
          this.setState(MODAL_STATE.NONE)
        }
      } catch (error) {
        displayHandled(error, this.ui.error)
        this.setState(MODAL_STATE.NONE)
      }
    },
    async cancelUnpaidReservation() {
      if (!this.isReservationUnpaid) return
      try {
        this.setState(MODAL_STATE.STATUS_CHANGING)
        const { reservation } = await this.reservationStore.setReservationStatus(
          this.selectedReservation!,
          ReservationStatus.CANCELED,
        )
        this.setSelectedReservation(reservation)
        this.setState(MODAL_STATE.NONE)
      } catch (error) {
        displayHandled(error, this.ui.error)
        this.setState(MODAL_STATE.NONE)
      }
    },
  },
  computed: {
    ...mapStores(useReservationStore),
    ...mapState(usePerformanceReservationStore, [
      'selectedPerformance',
      'selectedReservation',
      'selectedTableIds',
      'selectedTables',
      'selectedTablesName',
    ]),
    routeReservationId() {
      return Number(this.$route.params?.reservationId)
    },
    guestName() {
      return this.selectedReservation?.contact?.name || 'Guest'
    },
    isReservationUnpaid(): boolean {
      return this.selectedReservation?.status === ReservationStatus.UNPAID
    },
  },
})
</script>
